<template>
  <div>
    <b-form-group :label="label" :label-for="label">
      <v-select
        :loading="loading"
        :dir="locale === 'ar' ? 'rtl' : 'ltr'"
        :disabled="disabled"
        :multiple="multiple"
        :value="value"
        :required="required"
        :filterable="false"
        :options="transformedItems"
        :reduce="serializer"
        :label="isRelational ? undefined : textTitle"
        :placeholder="placeholder"
        @search="searchItem"
        @input="selected"
        attach
        clearable
        :close-on-select="closeOnSelect"
      >
        <template v-slot:option="option">
          <slot name="option" v-bind="option">
            <span v-if="textTitle.includes('item.name')">
              {{ option.item.name }}
            </span>
          </slot>
        </template>

        <template v-slot:selected-option="option">
          <slot name="selected-option" v-bind="option">
            <span v-if="textTitle.includes('item.name')">
              {{ option.item.name }}
            </span>
          </slot>
        </template>

        <template v-slot:no-options="props">
          <slot name="no-option" v-bind="props"></slot>
        </template>

        <template v-slot:list-footer="slotProps">
          <slot name="list-footer" v-bind="slotProps"></slot>
        </template>
      </v-select>
      <slot name="item-select"></slot>
      <b-form-invalid-feedback :state="!errorMessages">
        <span
          v-for="(err, index) in errorMessages"
          :key="index"
          v-text="err"
        ></span>
      </b-form-invalid-feedback>
    </b-form-group>
  </div>
</template>

<script>
import { BFormGroup, BFormInvalidFeedback } from "bootstrap-vue";
import { debounce } from "lodash";
import vSelect from "vue-select";

export default {
  components: {
    vSelect,
    BFormInvalidFeedback,
    BFormGroup,
  },
  props: {
    errorMessages: Array,
    value: Object | String,
    placeholder: String,
    label: String,
    endpoint: String,
    paramQuery: String,
    serializer: Function,
    required: Boolean,
    multiple: Boolean,
    textTitle: String,
    disabled: Boolean,
    otherQuery: String,
    employeeId: String,
    keyword: String,
    isRelational: Boolean,
    altSearch: String,
    noSort: Boolean,
    filter: String,
    include: String,
    closeOnSelect: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    items: [],
    loading: false,
    locale: localStorage.getItem("lng"),
    selection: [],
    searchValue: "",
    selectedItem: null,
  }),

  watch: {
    otherQuery() {
      if (!this.disabled) {
        this.getItems().then(() => {
          if (this.value) {
            const item = this.items.find((it) => it.id === this.value.id);
            if (!item) {
              this.items.push(this.value);
              this.selectedItem = this.value;
              this.$emit('input', this.value);
            } else {
              this.selectedItem = item;
              this.$emit('input', item);
            }
          }
        });
      }
    },
    include() {
      if (!this.disabled) {
        this.getItems().then(() => {
          if (this.value) {
            const item = this.items.find((it) => it.id === this.value.id);
            if (!item) {
              this.items.push(this.value);
              this.selectedItem = this.value;
              this.$emit('input', this.value);
            } else {
              this.selectedItem = item;
              this.$emit('input', item);
            }
          }
        });
      }
    },
    value: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.id) {
          this.selectedItem = newVal;
        } else {
          this.selectedItem = null;
        }
      },
    }
  },
  computed: {
    transformedItems() {
      // Get the base items list
      let itemsList = [...this.items];

      // If we have a selected value and it's not in the current items, add it
      if (this.value) {
        const selectedValue = Array.isArray(this.value) ? this.value : [this.value];
        selectedValue.forEach(val => {
          if (val && val.id && !itemsList.some(item => item.id === val.id)) {
            itemsList.unshift(val);
          }
        });
      }

      // Now transform the items as before
      if (this.textTitle.includes('.')) {
        const beforeText = this.getWordBeforeDot(this.textTitle);
        const afterText = this.getStringAfterDot(this.textTitle);

        return itemsList.map((item) => {
          const nestedProperty = item[beforeText];
          return {
            ...item,
            label: nestedProperty ? nestedProperty[afterText] : undefined,
          };
        });
      } else {
        return itemsList;
      }
    },
  },
  methods: {
    addToItems(item) {
      this.items.push(item);
    },
    getWordBeforeDot(inputString) {
      const dotIndex = inputString.indexOf(".");
      if (dotIndex !== -1) {
        return inputString.substring(0, dotIndex);
      } else {
        return ""; // Return an empty string if no dot is found
      }
    },
    getStringAfterDot(inputString) {
      const dotIndex = inputString.indexOf(".");
      if (dotIndex !== -1) {
        return inputString.substring(dotIndex + 1);
      } else {
        return ""; // Return an empty string if no dot is found
      }
    },
    selected(vals) {
      // Handle both single and multiple selections
      if (vals === null) {
        this.$emit('input', null);
      } else {
        this.$emit('input', vals);
      }
      this.$emit('hide', false);
      this.$emit('close', false);
      this.$emit('hidden', false);
    },
    async getItems(query = '', pageSize = 5) {
      this.loading = true;
      let url = `/${this.endpoint}?page=1&take=30&${
        this.noSort ? '' : 'sort=-id&'
      }${`filter[${this.altSearch ? this.altSearch : this.textTitle}]=${
        query ?? ''
      }`}`;
      if (this.include) {
        url += `&include=${this.include}`;
      }
      if (this.otherQuery) {
        url += `&${this.otherQuery}`;
      }
      if (this.isRelational) {
        url += `&filter[${this.paramQuery}]=${query ?? ''}`;
      }
      await this.getList(url);

      // Update items with new data
      this.items = this.all_data.data;
      this.loading = false;
    },
    searchItem: debounce(function (query) {
      if (!query && this.selectedItem) {
        const exists = this.items.some(item => item.id === this.selectedItem.id);
        if (!exists) {
          this.items.unshift(this.selectedItem);
        }
      }
      this.getItems(query, 500);
    }, 500),
    async findItem(id) {
      try {
        // Only proceed if we have a valid ID
        if (id !== undefined && id !== null) {
          await this.getList(`/${this.endpoint}/${id}`);
          // Verify data exists before trying to emit
          if (this.all_data) {
            const fetchedItem = this.all_data;

            // Add the fetched item to our items list if it doesn't already exist
            const itemExists = this.items.some(
              (item) => item.id === fetchedItem.id
            );
            if (!itemExists) {
              this.items.push(fetchedItem);
            }
            console.log(fetchedItem);
            this.value = fetchedItem;
            this.$emit("input", fetchedItem);
            return fetchedItem;
          } else {
            console.warn(`No data found for ${this.endpoint} with id ${id}`);
          }
        } else {
          console.warn("Attempted to find item with invalid id");
        }
        return null;
      } catch (error) {
        console.error(
          `Error fetching ${this.endpoint} item with id ${id}:`,
          error
        );
        return null;
      }
    },
    async initializeComponent() {
      try {
        // Load initial items list
        await this.getItems();

        // Skip if no value is set
        if (!this.value) return;

        // Handle different value types
        if (typeof this.value === "object" && this.value !== null) {
          if (this.value.id !== undefined) {
            await this.handleExistingValue(this.value.id);
          } else {
            // If value is an object without ID - custom handling
            console.warn("Value object is missing ID property", this.value);
          }
        } else if (
          typeof this.value === "number" ||
          typeof this.value === "string"
        ) {
          // Handle case where value is just an ID
          await this.handleExistingValue(this.value);
        }
      } catch (error) {
        console.error("Failed to initialize autocomplete component:", error);
      }
    },

    async handleExistingValue(id) {
      // Try to find item in already loaded items
      const matchedItem = this.items.find((item) => item.id === id);

      if (matchedItem) {
        // Item found in existing items list
        this.$emit("input", matchedItem);
        this.value = matchedItem;
      } else {
        // Item not found - try to fetch it directly
        // The findItem method now adds the fetched item to this.items
        await this.findItem(id);
      }
    },
  },

  mounted() {
    // Early return if component is disabled
    if (this.disabled) return;

    this.initializeComponent();
  },
};
</script>
<style scoped>
.v-select-dropdown {
  z-index: 9999999 !important; /* Ensure it's higher than the table's z-index */
}
.dropdown-menu {
  z-index: 1050 !important; /* Use a value high enough to overlay other components */
}
</style>
